import React, { Ref } from "react";
import styled from "styled-components";
import {
  Color,
  FontSize,
  FontWeight,
  GlossyThemeProvider,
  Spacing,
  ZIndexes,
  background,
  content,
  ellipsis,
  transparentize,
} from "@glossy";
import Button from "@ui-kit/button";

export type SortPosts = "date" | "reach" | "engagement";

const HEADER_HEIGHT = 72;

export interface IProps {
  onClose: () => void;
  children: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  options?: React.ReactNode;
}

const Root = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
`;

const Header = styled.div`
  top: 0;
  position: absolute;
  width: 100%;
  z-index: ${ZIndexes.AboveContent};
  height: ${HEADER_HEIGHT}px;
  background: ${background};
  padding: 12px 16px;
  display: flex;
  align-items: center;
  box-shadow: ${transparentize(0.8, Color.Black)} 0 1px 2px 0;
`;

const Content = styled.div`
  padding: 0 32px;
  height: calc(100vh - ${HEADER_HEIGHT}px);
  margin-top: ${HEADER_HEIGHT}px;
  width: 100vw;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${content};
  background-color: ${background};
`;

const StyledButton = styled(Button)`
  margin-right: ${Spacing.Medium};
  flex-shrink: 0;
`;

const Titles = styled.div`
  color: ${(props) => content(props, "secondary")};
  min-width: 0;
  flex-grow: 1;
`;

const Subtitle = styled.div`
  font-size: ${FontSize.Large};
  font-weight: ${FontWeight.Normal};
  color: ${content};
  ${ellipsis()};
`;

const Options = styled.div`
  display: flex;
  flex-shrink: 0;
  column-gap: ${Spacing.Small};
`;

const RadarlyPanelPosts = React.forwardRef(
  (
    { onClose, children, title, subtitle, options }: IProps,
    ref: Ref<HTMLDivElement>
  ) => (
    <Root>
      <GlossyThemeProvider name="surface">
        <Header>
          <StyledButton
            icon="cross"
            variant="tertiary"
            size="l"
            onClick={onClose}
          />
          <Titles>
            <div>{title}</div>
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
          </Titles>
          {options && <Options>{options}</Options>}
        </Header>
      </GlossyThemeProvider>
      <Content ref={ref}>{children}</Content>
    </Root>
  )
);

export default RadarlyPanelPosts;
